import * as _ from "lodash";
import { AnyAction } from "redux";
import {
	EXT_OFFSET,
	MIN_DIMENSION,
	PADDING_INSIDE_ATTRIBUTE_PLACEHOLDER_SHAPES,
	PERCENTAGE_HUNDRED,
	SPACE_BETWEEN_ATTRIBUTE_PLACEHOLDER_SHAPES,
	TEXT_STYLE_DEFAULTS,
} from "../../Constants";
import { IAnyObjectType } from "../../widgets/components/SelectionBox";
import { IExtents } from "../../widgets/interfaces/model/dataStructures/Extents";
import { IOffset } from "../../widgets/interfaces/model/dataStructures/Offset";
import {
	AutosizeMode,
	CanvasMode,
	CompoundLine,
	DashPresets,
	FillTypes,
	GeometryTypes,
	IPageObjectTypes,
	LineCap,
	PlaceholderTextFormat,
	ShapeType,
} from "../../widgets/interfaces/model/enumerations/index";
import { ICustomGeometry, IHandlePoint, IPenGeometry } from "../../widgets/interfaces/model/geometry/CustomGeometry";
import { IPresetGeometry } from "../../widgets/interfaces/model/geometry/PresetGeometry";
import { IShapeGuide } from "../../widgets/interfaces/model/geometry/ShapeGuide";
import { IShapePath } from "../../widgets/interfaces/model/geometry/ShapePath";
import {
	ICanvasDimension,
	INonUndoableAction,
	IPage,
	ISelection,
} from "../../widgets/interfaces/model/index";
import { IPageMeta } from "../../widgets/interfaces/model/PageMeta";
import { IGroupShape, IPicture } from "../../widgets/interfaces/model/PageObjects/index";
import { IShape } from "../../widgets/interfaces/model/PageObjects/Shape";
import { IPlaceholderFillData } from "../../widgets/interfaces/model/PlaceholderFillData";
import {
	IPageObjectProperties,
	IPictureFillProperties,
	IShapeProperties,
	ISolidFillProperties,
	ITransform2D,
} from "../../widgets/interfaces/model/properties/index";
import { ILineProperties } from "../../widgets/interfaces/model/properties/LineProperties";
import { IShapeTextBody } from "../../widgets/interfaces/model/text/ShapeTextbody";
import { PageObjectType } from "../../widgets/interfaces/model/Types";
import { IPosition } from "../../widgets/meta/Drag/CsiDragController";
import * as util from "../../widgets/util";
import {
	applyTextStyles,
	clearPlaceholderTextFormatting,
	clearTextFormatting,
	getDefaultFroalaHtml,
} from "../../widgets/util";
import PageHelper from "../PageHelper";
import * as selectionHelper from "../SelectionHelper";
import { ISetShapeTextStyle } from "./index";
import {
	addAttributeToObject,
	allowsText,
	calculateAdjustGroup,
	convertShapeToCustom,
	deepFindObjectById,
	deepGenerateNewUUIDs,
	filterSelectedObjects,
	filterUnLockedObjects,
	getBoundingBox,
	getDimensions,
	getImageExtents,
	getOuterGroup,
	getRotationFlipMatrix,
	getSelectedUnlockedObjects,
	manipulateDimensions,
	mapPointToMatrix,
	mapPointToShapeMatrix,
	moveObjectsToFront,
	removePlaceholdersMatchingKeys,
	restoreDefaultPlaceholderView,
	spaceBetweenTwoObjects,
	xor,
} from "./util";

/**
 * Recalculate extents of all groups
 */
export interface IAdjustGroupExtentsAction extends INonUndoableAction {
}

/**
 * Add page action interface
 */
export interface IAddPageAction extends AnyAction {
    /**
     * Index where to add the page. Base is 1.
     */
    index: number;

    /**
     * Page to be added.
     */
    page: IPage;
};

/**
 * Update page meta action interface
 */
export interface ISetPageMetaAction extends AnyAction {

    /**
     * Meta to be set.
     */
    meta: IPageMeta;

    /**
     * Selected page index.
     * Index starts with 1 instead of 0
     */
    selectedPage: number;
};

/**
 * Delete page action interface
 */
export interface IDeletePageAction extends AnyAction {
    page: number;
};

/**
 * Set pages action interface
 */
export interface ISetPageAction extends AnyAction {
    pages: IPage[];
}

/**
 * Set line action interface
 */
export interface ISetLineTypeAction extends AnyAction, ISelection {
    lineType: string;
}

/**
 * Set stroke width action interface
 */
export interface ISetStrokeWidthAction extends AnyAction, ISelection {
    strokeWidth: number;
}

/**
 * Set line cap action interface
 */
export interface ISetLineCapAction extends AnyAction, ISelection {
    headEnd: LineCap;
    tailEnd: LineCap;
}

/**
 * Rotate object action interface
 */
export interface IRotateObjectAction extends INonUndoableAction, ISelection {
    rotateAngle: number;
}

/**
 * Scale image to frame action interface
 */
export interface IScaleImageToFrameAction extends INonUndoableAction, ISelection {
    /**
     * Original aspect ratios
     */
    originalAspectRatios: any[];
    /**
     * Scaling type
     */
    scalingType: string;
}

/**
 * Scale object extends action interface
 */
export interface IScaleObjectExtendsAction extends INonUndoableAction, ISelection {
    /**
     * Width increase delta
     */
    cxOffset: number;
    /**
     * Height increase delta
     */
    cyOffset: number;
}

/**
 * Set object extends action interface
 */
export interface ISetObjectExtendsAction extends AnyAction, ISelection {
    extend: { cx?: number; cy?: number };
}

/**
 * Reset attribute placeholder extends action interface
 */
export interface IResetPlaceholderExtendsAction extends AnyAction, ISelection {
    objectId?: String;
    placeholder?: IGroupShape;
    transient?: boolean;
}

/**
 * Edit attribute placeholder action interface
 */
export interface IEditPlaceholderAction extends AnyAction, ISelection {
    pageObjects: IShape[];
}

/**
 * Remove migrated extends action interface
 */
export interface IRemoveMigratedExtents extends AnyAction {
    shapeIds: string[];
}

export interface ISetOffsetsAction extends AnyAction, ISelection {
    off: IOffset;
}

/**
 * Flip object action interface
 */
export interface IFlipObjectAction extends AnyAction, ISelection {
    flipDetails: IObjectFlip;
}

/**
 * Set dash line action interface
 */
export interface IDashLineAction extends AnyAction, ISelection {
    dashType: string;
}

/**
 * Flip object action interface
 */
export interface IAlignObjectAction extends AnyAction, ISelection, ICanvasDimension {
    alignOptions: string;
}

/**
 * Format Painter action interface
 */
export interface IFormatPainterAction extends AnyAction, ISelection {
    placeholderTextFormat: string;
    sourceShapeTextStyle: any;
    targetShapeId: string;
}

/**
 * Distribute object action interface
 */
export interface IDistributeObjectAction extends AnyAction, ISelection {
    dHorizontal: boolean;
}

/**
 * Change sort order action interface
 */
export interface IChangeOrderAction extends AnyAction, ISelection {
    pageObjects: PageObjectType[];
}

/**
 * Set object fill/stroke  action interface
 */
export interface ISetObjectColorAction extends AnyAction, ISelection {
    color: string;
}

/**
 * Set object shape action interface
 */
export interface ISetObjectShapeAction extends AnyAction, ISelection {
    shape: ShapeType;
}

/**
 * Translate object action interface
 */
export interface ITranslateAction extends INonUndoableAction, ISelection {
    dx: number;
    dy: number;
}

/**
 * Move object action interface
 */
export interface IMoveObjectsAction extends INonUndoableAction, ISelection {
    dx: number;
    dy: number;
}

/**
 * Paste object action interface
 */
export interface IPasteObjectAction extends AnyAction, ISelection {
    clipboard: PageObjectType[];
}
/**
 * Group shapes action interface
 */
export interface IGroupAction extends AnyAction, ISelection {
    isAttributePlaceholder?: boolean;
    autospace?: boolean;
    shapeId: string;
}

/**
 * Ungroup shapes action interface
 */
export interface IUnGroupAction extends AnyAction {
    groupdShapes: number[];
    selectedPage: number | undefined;
}

/**
 * Lock object action interface
 */
export interface ILockObjectAction extends AnyAction, ISelection {
    lock: boolean;
}

/** Set source rectangle refExt action interface */
export interface ISetSrcRectRefExtAction extends INonUndoableAction {
    /** Selected page index */
    selectedPage?: number | undefined;

    /** Selected page objects indices */
    selectedPageObjects: number[];

    /** Reference extents for image source rectangle */
    refExt: IExtents;
}

/** Change shape type action interface */
export interface IChangeShapeType extends INonUndoableAction {
    /** Selected page index */
    selectedPage?: number | undefined;

    /** Selected page objects indices */
    selectedPageObjects: number[];

    /** The new shape type */
    newShapeType: ShapeType;
}

/**
 * Delete shape by shapeId action interface
 */
export interface IDeleteShapeByIdAction extends INonUndoableAction, ISelection {
    shapeId: string;
}

/**
 * Delete objects action interface
 */
export interface IDeleteObjectsAction extends AnyAction, ISelection {
    groupSelectionIndices?: number[];
}

/**
 * Delete keys action interface
 */
export interface IRemoveKeysAction extends AnyAction {
    keys: string[];
}

export interface IAddImageToPlaceholderAction extends AnyAction {
    /**
     * The image to add to the placeholder
     */
    image: IAddImageRequest;
    /**
     * Id of placeholder to add the image to
     */
    objectId: String;
    /**
     * Selected page index.
     * Index starts with 1 instead of 0
     */
    selectedPage: number | undefined;
}

export interface IAddAttributeToPlaceholderAction extends AnyAction {
    /**
     * The attributes to add to the placeholder
     */
    attributes: IPlaceholderFillData[];
    /**
     * The node the attributes came from
     */
    node: string;
    /**
     * Id of placeholder to add the image to
     */
    objectId: String;
    /**
     * Selected page index.
     * Index starts with 1 instead of 0
     */
    selectedPage: number | undefined;
}

export interface IAddPageObjectAction extends AnyAction {
    /**
     * Which page object to be added.
     */
    pageObject: PageObjectType;
    /**
     * Selected page index.
     * Index starts with 1 instead of 0
     */
    selectedPage?: number | undefined;
}

export interface IAddPageObjectsAction extends AnyAction {
    /**
     * Page objects to be added.
     */
    pageObjects: PageObjectType[];
    /**
     * Selected page index.
     * Index starts with 1 instead of 0
     */
    selectedPage?: number | undefined;
}

/**
 * Set curve points action, it will update the curve points for selected shape.
 */
export interface ISetCurvePointsAction extends AnyAction, ISelection {
    /**
     *  New set of handle points for shape, if handle points are to be changed.
     */
    handlePoints?: IHandlePoint[];
    /**
     *  New path list for shape.
     */
    pathLst: IShapePath[];
}

/**
 * Set geometry action, updates the state path list
 */
export interface ISetGeometryAction extends AnyAction, ISelection {
    /**
     *  New path list for shape.
     */
    pathLst: IShapePath[];
}

/**
 * Set handle points action, sets the shape's handle points to the provided list
 */
export interface ISetHandlePointsAction extends AnyAction, ISelection {
    /**
     *  New handle point list for shape.
     */
    pointLst: IHandlePoint[];
}

/** Set shape adjust values */
export interface ISetShapeAdjustValues extends ISelection {

    /** Adjust value list for selected shape, if any */
    avList: IShapeGuide[];

    /** Source rectangle reference extents */
    refExt: IExtents;
}

/** Set shape adjust values */
export interface ISetShapeAutosizeValues extends ISelection {
    autosize: AutosizeMode;
    resizeForContent: boolean;
}

/**
 * Set shape text content action, it will update the text content for
 * the object at specified index.
 */
export interface ISetShapeTextContentAction extends INonUndoableAction, ISelection {
    content: string;
    objectKey: number;
}

/**
 * Set shape text style action interface
 */
export interface ISetShapeTextStyleAction extends INonUndoableAction, ISelection {
    mode: CanvasMode;
}

/**
 * Flip object interface
 */
export interface IObjectFlip {
    flipH?: boolean;
    flipV?: boolean;
}

/** Toggle autospace for placeholders */
export interface IToggleAutospace extends INonUndoableAction, ISelection { }

/** Toggle labels for placeholders */
export interface IToggleLabels extends INonUndoableAction, ISelection { }

/** Toggle placeholder labels optionally based on edit mode and value content */
export interface IToggleLabelsOptionally extends INonUndoableAction, ISelection {
    canvasEditMode: boolean;
}

/**
 * Scale object action interface
 */
export interface IScaleObjectAction extends INonUndoableAction, ISelection {
    /** Delta X */
    dX: number;
    /** Delta Y */
    dY: number;

    /**
     * When crop mode is on, we manipulate cropping shape and image with respect to their edges. In this context,
     * both are in same frame of reference and for computation purpose we dont need to apply reverse matrix like dX
     * value. Hence, an explicit property to hold the cropDX.
     */
    cropDX: number;

    /**
     * Same as cropDX. An explicit property to hold cropDY.
     */
    cropDY: number;

    /** Delta Width */
    dWidth: number;
    /** Delta Height */
    dHeight: number;

    /**
     * Flag for object (shape) scale in picture edit mode.
     * When this flag is true, it means that cropping shape is being scaled.
     */
    isCroppingShapeScale: boolean;

    /**
     * Current mode for the canvas.
     */
    mode: CanvasMode;

    /** The Source Rectangle WRT extends */
    refExt?: IExtents;

    /**
     * When crop mode is on, we manipulate cropping shape and image with respect to their edges. In this context,
     * both are in same frame of reference and for computation purpose we dont need to apply reverse matrix like dX
     * value. Hence, if model shape boundary are different from render shape boundary (Ex. Callout Shape Image ) so
     * an explicit property to hold the cropDWidth.
     */
    cropDWidth: number;

    /**
     * Same as cropDWidth. An explicit property to hold cropDHeight.
     */
    cropDHeight: number;
}

/**
 * Sort pages action interface
 */
export interface ISortPagesAction extends AnyAction {
    sortedPages: IPage[];
}

export interface IAddImageRequest {
    height: number;
    imageLink: string;
    width: number;
    x: number;
    y: number;
}

/**
 * Add a page.
 * Page is appended to existing Page[]
 * @param state {ICanvasEditorState['pages']} Current state for CanvasPage reducer
 * @param action {IAddPageAction} Reducer payload to add page
 * @param action.type {String} 'ADD_PAGE'
 * @param action.index {number} The index for the page to the base 1.
 * @param action.page {IPage} The Page to add
 * @returns {ICanvasEditorState['pages']} New state for CanvasPage reducer
 */
export const addPage = (state: IPage[], action: IAddPageAction): IPage[] => {
	const newState = _.cloneDeep(state);
	newState.splice((action.index - 1), 0, action.page);
	return newState;
};

/**
 * Calculate all group extents
 * @param state {ICanvasEditorState['pages']} Current state for CanvasPage reducer
 * @param action {IAdjustGroupExtentsAction} Action to adjust all group extents
 * @returns {ICanvasEditorState['pages']} New state for CanvasPage reducer
 */
export const calculateGroupExtents = (state: IPage[], action: IAdjustGroupExtentsAction): IPage[] => {
	const newState = _.cloneDeep(state);
	const page = newState[action.selectedPage as number - 1];

	_.each(page.pageObjects, (object) => {
		const group = object as any;
		if (group.childObjects) {
			calculateAdjustGroup(group);
		}
	});

	return newState;
};

/**
 * Update page meta data.
 * @param state {ICanvasEditorState['pages']} Current state for CanvasPage reducer
 * @param action {ISetPageMetaAction} Reducer payload to update page meta.
 */
export const setPageMeta = (state: IPage[], action: ISetPageMetaAction): IPage[] => {
	const newState = _.cloneDeep(state);
	const page = newState[action.selectedPage as number - 1];
	page.meta = action.meta;
	return newState;
};

/**
 * Delete specified page.
 * Page is removed from existing Page[].
 * @param state {ICanvasEditorState['pages']} Current state for CanvasPage reducer
 * @param action {IDeletePageAction} Reducer payload to delete page
 * @param action.page {Number} The Page to delete
 * @returns {ICanvasEditorState['pages']} New state for CanvasPage reducer
 */
export const deletePage = (state: IPage[], action: IDeletePageAction): IPage[] => {
	const newState = [...state];
	_.remove(newState, (value, index, array) => index === (action.page - 1));
	return newState;
};

/**
 * Set pages.
 * Pages are replaced with new set of pages.
 * Note: all existing pages will be removed from store.
 * @param state {ICanvasEditorState['pages']} Current state for CanvasPage reducer
 * @param action {ISetPageAction} Reducer payload to set pages
 * @param action.type {String} 'SET_PAGES'
 * @param action.pages {IPage[]} The Pages to set
 * @returns {ICanvasEditorState['pages']} New state for CanvasPage reducer
 */
export const setPages = (state: IPage[], action: ISetPageAction): IPage[] => {
	return [...action.pages];
};

/**
 * Dummy reducer to record history for ICanvasEditorState['pages'].
 * @param state {ICanvasEditorState['pages']} Current state for CanvasPage reducer
 * @param action {AnyAction} Reducer payload
 */
export const pageDummyAction = (state: IPage[], action: AnyAction): IPage[] => {
	return [...state];
};

/**
 * Add attribute to placeholder
 * @param state Current state for CanvasPage reducer
 * @param action {IAddAttributeToPlaceholderAction} Reducer payload to add image to placeholder
 * @returns New state for CanvasPage reducer
 */
export const addAttributeToPlaceholder = (state: IPage[], action: IAddAttributeToPlaceholderAction): IPage[] => {
	const newState = _.cloneDeep(state);
	const page = newState[action.selectedPage as number - 1];
	const resultObj = deepFindObjectById(page.pageObjects, action.objectId);
	const groupObject = resultObj.pageObjects[resultObj.x];
	if (groupObject.childObjects) {
		groupObject.childObjects.forEach( (pageObject: IShape, i: number) => {
			// set node URL on page object
			pageObject.nodeURL = action.node;

			if (pageObject.isTextShape) {
				addAttributeToObject(pageObject, action.attributes[i]);
				// re-evaluate label status
				util.togglePlaceholderLabel(pageObject, pageObject.showLabel);
			} else if (action.attributes[i].restoreImagePlaceholder) {
				// reset image placeholder to default but keep the URL because in
				// this case because we are filling the placeholder from c8
				restoreDefaultPlaceholderView(pageObject, true);
			}
		});
	}
	return newState;
};

/**
 * Add image to placeholder
 * @param state Current state for CanvasPage reducer
 * @param action {IAddImageToPlaceholderAction} Reducer payload to add image to placeholder
 * @returns New state for CanvasPage reducer
 */
export const addImageToPlaceholder = (state: IPage[], action: IAddImageToPlaceholderAction): IPage[] => {
	const newState = _.cloneDeep(state);
	const page = newState[action.selectedPage as number - 1];
	const { pageObjects, x } = deepFindObjectById(page.pageObjects, action.objectId);
	const po = pageObjects[x];

	const newImageObject = PageHelper.makeImageFromPlaceholder(po as IShape, action.image);

	pageObjects[x] = newImageObject;
	return newState;
};

/**
 * Add page object to state
 * @param state Current state for CanvasPage reducer
 * @param action {IAddPageObjectAction} Reducer payload to add page object
 * @returns New state for CanvasPage reducer
 */
export const addPageObject = (state: IPage[], action: IAddPageObjectAction): IPage[] => {
	const newState = _.cloneDeep(state);
	const page = newState[action.selectedPage as number - 1];
	page.pageObjects.push(action.pageObject);
	return newState;
};

/**
 * Add page object to state
 * @param state Current state for CanvasPage reducer
 * @param action {IAddPageObjectsAction} Reducer payload to add page object
 * @returns New state for CanvasPage reducer
 */
export const addPageObjects = (state: IPage[], action: IAddPageObjectsAction): IPage[] => {
	const newState = _.cloneDeep(state);
	const page = newState[action.selectedPage as number - 1];
	_.each(action.pageObjects, (pageObject) => {
		page.pageObjects.push(pageObject);
	});
	return newState;
};

/**
 * Set shape Adjust shape as per adjust handler
 * @param state Current state for CanvasPage reducer
 * @param action Reducer payload to to set shape adjust values
 */
export const setShapeAdjustValues = (state: IPage[], action: ISetShapeAdjustValues): IPage[] => {
	const newState = _.cloneDeep(state);
	const page = newState[action.selectedPage as number - 1];
	const unlockedSelectedObjects = getSelectedUnlockedObjects(page, action);
	unlockedSelectedObjects.forEach((pageobject, index) => {
		(pageobject as IShape).spPr.geometry.avList = action.avList;
		if (pageobject.type === IPageObjectTypes.SHAPE && pageobject.isPicture) {
			const { srcRect } = (pageobject.spPr.fill as IPictureFillProperties);
			srcRect.refExt = action.refExt;
		}
	});
	return newState;
};

/**
 * Set shape autosize values
 * @param state Current state for CanvasPage reducer
 * @param action Reducer payload to to set shape adjust values
 */
export const setShapeAutosizeValues = (state: IPage[], action: ISetShapeAutosizeValues): IPage[] => {
	const newState = _.cloneDeep(state);
	const page = newState[action.selectedPage as number - 1];
	const unlockedSelectedObjects = getSelectedUnlockedObjects(page, action);
	_applyShapeAutosizeValues(page, action, unlockedSelectedObjects, action.autosize, action.resizeForContent);
	return newState;
};

/**
 * Apply Autosize values to all nested shapes.
 * @param {PageObjectType[]} pageObjects
 * @param {AutosizeMode} autosize
 * @param {boolean} resizeForContent - flag to resize extents for content
 */
const _applyShapeAutosizeValues = (
	page: IPage,
	action: ISetShapeAutosizeValues,
	pageObjects: PageObjectType[],
	autosize: AutosizeMode,
	resizeForContent: boolean,
) => {
	_.each(pageObjects, (pageObject) => {
		if (pageObject.type === IPageObjectTypes.SHAPE && pageObject.isTextShape) {
			const shape = pageObject as IShape;
			shape.spPr.autosize = autosize;
			const outerGroup = getOuterGroup(page, action) as IGroupShape;
			if (outerGroup.autospace) {
				outerGroup.autospace = false;
			}
			util.applyAutosizeExtents(shape, resizeForContent);
		} else if (pageObject.type === IPageObjectTypes.GROUP_SHAPE) {
			if (pageObject.autospace) {
				pageObject.autospace = false;
			}
			_applyShapeAutosizeValues(page, action, pageObject.childObjects, autosize, resizeForContent);
			calculateAdjustGroup(pageObject);
		}
	});
};

/**
 * Apply scale to child shapes of shape group
 * @param groupObject The shape group
 * @param dWidth width scaling
 * @param dHeight height scaling
 */
const _groupScale = (groupObject: IGroupShape, dWidth: number, dHeight: number) => {
	_.each(groupObject.childObjects, (child, index) => {
		let childOffX = child.spPr.xfrm.off.x;
		let childOffY = child.spPr.xfrm.off.y;
		let childWidth = child.spPr.xfrm.ext.cx;
		let childHeight = child.spPr.xfrm.ext.cy;
		// Here we are using MIN_DIMENSION iff childWidth or childHeight is zero.
		// to avoid dW and dH becoming zero.
		const dW = (childWidth || MIN_DIMENSION) * dWidth;
		const dH = (childHeight || MIN_DIMENSION) * dHeight;

		const dX = childOffX * dWidth;
		const dY = childOffY * dHeight;

		if (child.type === IPageObjectTypes.GROUP_SHAPE) {
			_groupScale(child, dWidth, dHeight);
		}
		const oldChildOffX = childOffX;
		const oldChildOffY = childOffY;
		// translate child;
		childOffX += dX;
		childOffY += dY;

		// scale child shape
		childWidth += dW;
		childHeight += dH;

		const spPr = child.spPr as IShapeProperties;
		const isFlipped: boolean = (childWidth < 0 || childHeight < 0);

		if (spPr.geometry?.type === GeometryTypes.CUSTOM || spPr.geometry?.type === GeometryTypes.PEN) {
			const path = spPr.geometry.pathLst as IShapePath[];
			const penGeometry = spPr.geometry as IPenGeometry;
			util.updateShapePathListPercentage(path, dWidth, dHeight, isFlipped, penGeometry.handlePoints);
		}

		child.spPr.xfrm.ext.cx = Math.abs(childWidth);
		child.spPr.xfrm.ext.cy = Math.abs(childHeight);
		// Flag to check if the old offset sign got changed after applying delta.
		const isOffXSignChanged: boolean = (childOffX < 0 && oldChildOffX > 0) || (childOffX > 0 && oldChildOffX < 0);
		const isOffYSignChanged: boolean = (childOffY < 0 && oldChildOffY > 0) || (childOffY > 0 && oldChildOffY < 0);
		// Keep offset sign same as it was before flipped.
		child.spPr.xfrm.off.x = childOffX * ((isOffXSignChanged && isFlipped) ? -1 : 1);
		child.spPr.xfrm.off.y = childOffY * ((isOffYSignChanged && isFlipped) ? -1 : 1);
	});
};

/**
 * Translate object.
 * @param state {ICanvasEditorState['pages']} Current state for CanvasPage reducer
 * @param action {ITranslateAction} Reducer payload to translate object
 * @param action.type {String} 'TRANSLATE_OBJECTS'
 * @param action.selectedPage {number} current selected page
 * @param action.selectedPageObjects {number[]} Indices of current selected objects
 * @param action.x {number}
 * @param action.y {number}
 * @returns {ICanvasEditorState['pages']} New state for CanvasPage reducer
 */
export const translateObjects = (state: IPage[], action: ITranslateAction) => {
	const newState = _.cloneDeep(state);
	const page = newState[action.selectedPage as number - 1];
	const unlockedSelectedObjects = getSelectedUnlockedObjects(page, action);
	const selectedGroup = getOuterGroup(page, action) as IGroupShape;

	let translateX = action.dx;
	let translateY = action.dy;
	if (action.groupSelectionIndices !== undefined && action.groupSelectionIndices.length > 0) {
		// If we are moving a subgroup item, have to move it its relative rotated amount
		const radians = selectedGroup.spPr.xfrm.rot * (Math.PI / 180) * -1;
		translateX = action.dx * Math.cos(radians) - action.dy * Math.sin(radians);
		translateY = action.dx * Math.sin(radians) + action.dy * Math.cos(radians);

		if (selectedGroup.spPr.xfrm.flipH) {
			translateX = -translateX;
		}
	}

	unlockedSelectedObjects.forEach((pageObject: any) => {
		// turn off autospace if this is an attribute placeholder and a subelement is moved
		if (pageObject.attributeKey && selectedGroup.autospace) {
			selectedGroup.autospace = false;
		}
		const { off } = pageObject.spPr.xfrm;
		off.x += translateX;
		off.y += translateY;
	});
	return newState;
};

/**
 * Paste objects.
 * @param state {ICanvasEditorState['pages']} Current state for CanvasPage reducer
 * @param action {ICopyObjectAction} Reducer payload to translate object
 * @param action.type {String} 'PASTE_OBJECTS'
 * @param action.selectedPage {number} current selected page
 * @param action.selectedPageObjects {number[]} Indices of current selected objects
 * @returns {ICanvasEditorState['pages']} New state for CanvasPage reducer
 */
export const paste = (state: IPage[], action: IPasteObjectAction) => {
	const newState = _.cloneDeep(state);
	const newClipboard = _.cloneDeep(action.clipboard);
	const { selectedPage } = action;
	const page = newState[selectedPage as number - 1];
	deepGenerateNewUUIDs(newClipboard);
	_.each(newClipboard, (pageObject) => {
		page.pageObjects.push(pageObject);
	});
	return newState;
};

/**
 * Set objects fill.
 * @param state {ICanvasEditorState['pages']} Current state for CanvasPage reducer
 * @param action {ISetObjectFillAction} Reducer payload to set objects fill
 * @param action.type {String} 'SET_FILL'
 * @param action.selectedPage {number} current selected page
 * @param action.selectedPageObjects {number[]} Indices of current selected objects
 * @param action.color {string} fill color
 * @returns {ICanvasEditorState['pages']} New state for CanvasPage reducer
 */
export const setObjectsFill = (state: IPage[], action: ISetObjectColorAction) => {
	const newState = _.cloneDeep(state);
	const page = newState[action.selectedPage as number - 1];
	const unlockedSelectedObjects = getSelectedUnlockedObjects(page, action);
	_applyFillProperties(unlockedSelectedObjects, {
		color: action.color,
		type: FillTypes.SOLID_FILL,
	});
	return newState;
};

/**
 * Set objects shape.
 * @param state {ICanvasEditorState['pages']} Current state for CanvasPage reducer
 * @param action {ISetObjectShapeAction} Reducer payload to set objects shape
 * @param action.type {String} 'SET_SHAPE'
 * @param action.selectedPage {number} current selected page
 * @param action.selectedPageObjects {number[]} Indices of current selected objects
 * @param action.presetShape {ShapeType} shape
 * @returns {ICanvasEditorState['pages']} New state for CanvasPage reducer
 */
export const setObjectsShape = (state: IPage[], action: ISetObjectShapeAction) => {
	const newState = _.cloneDeep(state);
	const page = newState[action.selectedPage as number - 1];
	const unlockedSelectedObjects = getSelectedUnlockedObjects(page, action);
	_applyShapeProperty(unlockedSelectedObjects, action.presetShape);
	return newState;
};

/**
 * Set objects line color.
 * @param state {ICanvasEditorState['pages']} Current state for CanvasPage reducer
 * @param action {ISetObjectFillAction} Reducer payload to set line colour
 * @param action.type {String} 'SET_LINE_COLOR'
 * @param action.selectedPage {number} current selected page
 * @param action.selectedPageObjects {number[]} Indices of current selected objects
 * @param action.color {string} line color
 * @returns {ICanvasEditorState['pages']} New state for CanvasPage reducer
 */
export const setLineColor = (state: IPage[], action: ISetObjectColorAction) => {
	const newState = _.cloneDeep(state);
	const page = newState[action.selectedPage as number - 1];
	const unlockedSelectedObjects = getSelectedUnlockedObjects(page, action);
	_applyLineProperties(unlockedSelectedObjects, {
		fill: {
			color: action.color,
			type: FillTypes.SOLID_FILL,
		},
	});
	return newState;
};

/**
 * Set line type
 * @param state {ICanvasEditorState['pages']} Current state for CanvasPage reducer
 * @param action {IStrokeWidthAction} Reducer payload to set line type
 * @param action.type {String} 'SET_LINE_TYPE'
 * @param action.selectedPage {number} current selected page
 * @param action.selectedPageObjects {number[]} Indices of current selected objects
 * @param action.lineType {string} line Type
 * @returns {ICanvasEditorState['pages']} New state for CanvasPage reducer
 */
export const setLineType = (state: IPage[], action: ISetLineTypeAction) => {
	const newState = _.cloneDeep(state);
	const page = newState[action.selectedPage as number - 1];
	const unlockedSelectedObjects = getSelectedUnlockedObjects(page, action);
	const lineType = action.lineType as CompoundLine;
	_applyLineProperties(unlockedSelectedObjects, { cmpd: lineType });
	return newState;
};

/**
 * Set stroke width
 * @param state {ICanvasEditorState['pages']} Current state for CanvasPage reducer
 * @param action {IStrokeWidthAction} Reducer payload to set line width
 * @param action.type {String} 'SET_STROKE_WIDTH'
 * @param action.selectedPage {number} current selected page
 * @param action.selectedPageObjects {number[]} Indices of current selected objects
 * @param action.strokeWidth {number} stroke width
 * @returns {ICanvasEditorState['pages']} New state for CanvasPage reducer
 */
export const setStrokeWidth = (state: IPage[], action: ISetStrokeWidthAction) => {
	const newState = _.cloneDeep(state);
	const page = newState[action.selectedPage as number - 1];
	const unlockedSelectedObjects = getSelectedUnlockedObjects(page, action);
	_applyLineProperties(unlockedSelectedObjects, { W: action.strokeWidth });
	return newState;
};

/**
 * Translate cropped image
 * @param state Current state for CanvasPage reducer
 * @param action Reducer payload to translate cropped image
 */
export const translateCroppedImage = (state: IPage[], action: ITranslateAction) => {
	const newState = _.cloneDeep(state);
	const page = newState[action.selectedPage as number - 1];
	const unlockedSelectedObjects = getSelectedUnlockedObjects(page, action);
	let groupObjectRot = 0;
	let groupObjectFlip = false;
	if (action.groupSelectionIndices !== undefined && action.groupSelectionIndices.length > 0) {
		groupObjectRot = page.pageObjects[action.selectedPageObjects[0]].spPr.xfrm.rot;
		groupObjectFlip = page.pageObjects[action.selectedPageObjects[0]].spPr.xfrm.flipH;
	}
	// Although, there would be only one object active for cropping purpose. Reducer will not consider that assumption
	// and will work on unlockedSelectedObjects.
	unlockedSelectedObjects.forEach((object, index) => {
		// Perform operation if object is shape and used for picture
		if (object.type === IPageObjectTypes.SHAPE && object.isPicture) {
			const { srcRect } = (object.spPr.fill as IPictureFillProperties);
			// consider flip and angle from ghost shape (srcRect) as events are generated from ghost shape
			const mappedDelta =
                mapPointToMatrix(getRotationFlipMatrix(
                	srcRect.rot + groupObjectRot,
                	xor(srcRect.flipH, groupObjectFlip),
                ), { x: action.dx, y: action.dy });
			// Considering source rectangle refference extents(refExt) if exits or else shape extents.
			const { cx, cy } = srcRect.refExt || object.spPr.xfrm.ext;
			const dyPerHeight = mappedDelta.y * PERCENTAGE_HUNDRED / cy;
			const dxPerWidth = mappedDelta.x * PERCENTAGE_HUNDRED / cx;

			srcRect.t = srcRect.t + dyPerHeight;
			srcRect.l = srcRect.l + dxPerWidth;

			srcRect.r = srcRect.r - dxPerWidth;
			srcRect.b = srcRect.b - dyPerHeight;
		}
	});
	return newState;
};

/**
 * Apply line properties to all nested shapes.
 * @param {PageObjectType[]} pageObjects
 * @param {ILineProperties} lineStyles Partial line properties objects.
 */
const _applyLineProperties = (pageObjects: PageObjectType[], lineStyles: ILineProperties) => {
	_.each(pageObjects, (pageObject) => {
		if ((pageObject.type === IPageObjectTypes.SHAPE ||
            pageObject.type === IPageObjectTypes.CONNECTION_SHAPE) &&
            !pageObject.imagePlaceholder) {
			const ln = pageObject.spPr.ln || {};
			_.assign(ln, _.cloneDeep(lineStyles));
		} else {
			if (pageObject.type === IPageObjectTypes.GROUP_SHAPE) {
				_applyLineProperties(pageObject.childObjects, lineStyles);
			}
		}
	});
};

/**
 * Apply line properties to all nested shapes.
 * @param {PageObjectType[]} pageObjects
 * @param {IFillProperties} lineStyles Partial line properties objects.
 */
const _applyFillProperties = (pageObjects: PageObjectType[], fillStyles: ISolidFillProperties) => {
	_.each(pageObjects, (pageObject) => {
		if (pageObject.type === IPageObjectTypes.SHAPE && !pageObject.imagePlaceholder) {
			const fill = pageObject.spPr.fill || {};
			_.merge(fill, _.cloneDeep(fillStyles));
			pageObject.spPr.fill = fill as IPictureFillProperties;
		} else {
			if (pageObject.type === IPageObjectTypes.GROUP_SHAPE) {
				_applyFillProperties(pageObject.childObjects, fillStyles);
			}
		}
	});
};

/**
 * Apply shape properties to all nested shapes.
 * @param {PageObjectType[]} pageObjects
 * @param {ShapeType} presetShape
 */
const _applyShapeProperty = (pageObjects: PageObjectType[], presetShape: ShapeType) => {
	_.each(pageObjects, (pageObject) => {
		if (pageObject.type === IPageObjectTypes.SHAPE) {
			pageObject.spPr.geometry.presetShape = presetShape;
		} else {
			if (pageObject.type === IPageObjectTypes.GROUP_SHAPE) {
				_applyShapeProperty(pageObject.childObjects, presetShape);
			}
		}
	});
};

/**
 * Set line cap
 * @param state {ICanvasEditorState['pages']} Current state for CanvasPage reducer
 * @param action {IStrokeWidthAction} Reducer payload to set line cap
 * @param action.type {String} 'SET_LINE_CAP'
 * @param action.selectedPage {number} current selected page
 * @param action.selectedPageObjects {number[]} Indices of current selected objects
 * @param action.headEnd {LineCap}
 * @param action.tailEnd {LineCap}
 * @returns {ICanvasEditorState['pages']} New state for CanvasPage reducer
 */
export const setLineCap = (state: IPage[], action: ISetLineCapAction) => {
	const newState = _.cloneDeep(state);
	const page = newState[action.selectedPage as number - 1];
	const unlockedSelectedObjects = getSelectedUnlockedObjects(page, action);
	_applyLineProperties(unlockedSelectedObjects, { headEnd: action.headEnd, tailEnd: action.tailEnd });
	return newState;
};

/**
 * Set source rectangle refExt
 * @param state Current state for CanvasPage reducer
 * @param action Reducer payload to set source rectangle refExt
 * @returns New state for CanvasPage reducer
 */
export const setSrcRectRefExt = (state: IPage[], action: ISetSrcRectRefExtAction): IPage[] => {
	const newState = _.cloneDeep(state);
	const page = newState[action.selectedPage as number - 1];
	const unlockedSelectedObjects = getSelectedUnlockedObjects(page, action);
	// Operate on unlocked objects only
	const shapes = _.filter(unlockedSelectedObjects, (shape) =>
		shape.type === IPageObjectTypes.SHAPE && shape.spPr.geometry.type === GeometryTypes.PRESET);
	_.forEach(shapes, (pageObject) => {
		// If its an image then set its source rectangle
		if (pageObject.type === IPageObjectTypes.SHAPE && pageObject.isPicture) {
			const { srcRect } = (pageObject.spPr.fill as IPictureFillProperties);
			srcRect.refExt = action.refExt;
		}
	});
	return newState;
};

/**
 * Change shape type of current selected shapes
 * @param state Current state of canvas page reducer
 * @param action Change shape type action
 */
export const changeShapeType = (state: IPage[], action: IChangeShapeType) => {
	const newState = _.cloneDeep(state);
	const page = newState[action.selectedPage as number - 1];
	const unlockedSelectedObjects = getSelectedUnlockedObjects(page, action);
	// Change selected unlocked preset shapes only
	const shapes = _.filter(unlockedSelectedObjects, (shape) =>
		shape.type === IPageObjectTypes.SHAPE);
	_.forEach(shapes, (pageObject) => {
		const { spPr } = pageObject as IShape;
		(pageObject as IShape).hasBeenEdited = true;
		const preGeo = spPr.geometry as IPresetGeometry;
		preGeo.presetShape = action.newShapeType;
		preGeo.avList = undefined;
		preGeo.pathLst = undefined;
		preGeo.type = GeometryTypes.PRESET;

		// If the its an image then set its source rectangle refExt to be undefined. This will allow render to set it
		// appropriately as refExt would be available at render time only.
		if (pageObject.type === IPageObjectTypes.SHAPE && pageObject.isPicture) {
			const { srcRect } = (pageObject.spPr.fill as IPictureFillProperties);
			srcRect.refExt = undefined;
		}
	});
	return newState;
};

/**
 * Change shape type of current selected shapes
 * @param state Current state of canvas page reducer
 */
export const makeShapeCustom = (state: IPage[], action: IChangeShapeType) => {
	const newState = _.cloneDeep(state);
	const page = newState[action.selectedPage as number - 1];
	const selectedObjects = page.pageObjects.filter(filterSelectedObjects(action.selectedPageObjects));
	const unlockedSelectedObjects = selectedObjects.filter(filterUnLockedObjects);
	// Change selected unlocked preset shapes only
	const shapes = _.filter(unlockedSelectedObjects, (shape) =>
		shape.type === IPageObjectTypes.SHAPE && shape.spPr.geometry.type === GeometryTypes.PRESET);
	_.forEach(shapes, (pageObject) => {
		const selectedShape = pageObject as IShape;
		convertShapeToCustom(selectedShape);
	});
	return newState;
};

/**
 * Rotate objects
 * @param state {ICanvasEditorState['pages']} Current state for CanvasPage reducer
 * @param action {IStrokeWidthAction} Reducer payload to rotate object
 * @param action.type {String} 'ROTATE_OBJECT'
 * @param action.selectedPage {number} current selected page
 * @param action.selectedPageObjects {number[]} Indices of current selected objects
 * @param action.rotateAngle {number} rotation angle
 * @returns {ICanvasEditorState['pages']} New state for CanvasPage reducer
 */
export const rotateObjects = (state: IPage[], action: IRotateObjectAction) => {
	const newState = _.cloneDeep(state);
	const page = newState[action.selectedPage as number - 1];
	const unlockedSelectedObjects = getSelectedUnlockedObjects(page, action);
	unlockedSelectedObjects.forEach((pageObject, index) => {
		pageObject.spPr.xfrm.rot = (pageObject.spPr.xfrm.rot + action.rotateAngle) % 360;
		// Transfer rotation to srcRect, too, if its an image object
		if (pageObject.type === IPageObjectTypes.SHAPE && pageObject.isPicture) {
			const { srcRect } = (pageObject.spPr.fill as IPictureFillProperties);
			srcRect.rot = (srcRect.rot + action.rotateAngle) % 360;
		}
	});
	return newState;
};

/**
 * Flip objects
 * @param state {ICanvasEditorState['pages']} Current state for CanvasPage reducer
 * @param action {IStrokeWidthAction} Reducer payload to flip object
 * @param action.type {String} 'FLIP_OBJECT'
 * @param action.selectedPage {number} current selected page
 * @param action.selectedPageObjects {number[]} Indices of current selected objects
 * @param action.flipDetails {IObjectFlip}
 * @returns {ICanvasEditorState['pages']} New state for CanvasPage reducer
 */
export const flipObjects = (state: IPage[], action: IFlipObjectAction) => {
	const newState = _.cloneDeep(state);
	const page = newState[action.selectedPage as number - 1];
	const unlockedSelectedObjects = getSelectedUnlockedObjects(page, action);
	unlockedSelectedObjects.forEach((pageObject, index) => {
		if (action.flipDetails) {
			const rotation = pageObject.spPr.xfrm.rot;

			if (action.flipDetails.flipH) {
				pageObject.spPr.xfrm.flipH = !pageObject.spPr.xfrm.flipH;
				pageObject.spPr.xfrm.rot = (360 - rotation) % 360;
			}

			// At model level, flipV = flipH + 180 degree, but we keep track of it
			// for use in keeping image placeholders displaying text upright
			if (action.flipDetails.flipV) {
				pageObject.spPr.xfrm.flipH = !pageObject.spPr.xfrm.flipH;
				pageObject.spPr.xfrm.rot = (180 - rotation) % 360;
				pageObject.spPr.xfrm.flipV = !pageObject.spPr.xfrm.flipV;
			}

			// Transfer the flip and rotation to srcRect
			if (pageObject.type === IPageObjectTypes.SHAPE && pageObject.isPicture
                && (action.flipDetails.flipH || action.flipDetails.flipV)) {
				const { srcRect } = (pageObject.spPr.fill as IPictureFillProperties);

				if (action.flipDetails.flipH) {
					srcRect.flipH = !srcRect.flipH;
					srcRect.rot = (360 - srcRect.rot) % 360;
				}

				// At model level, flipV = flipH + 180 degree
				if (action.flipDetails.flipV) {
					srcRect.flipH = !srcRect.flipH;
					srcRect.rot = (180 - srcRect.rot) % 360;
				}
			}
		}
	});
	return newState;
};

/**
 * Set dash property to objects
 * @param state {ICanvasEditorState['pages']} Current state for CanvasPage reducer
 * @param action {IDashLineAction} Reducer payload to set line dash style
 * @param action.type {String} 'SET_LINE_DASH'
 * @param action.selectedPage {number} current selected page
 * @param action.selectedPageObjects {number[]} Indices of current selected objects
 * @param action.dashType {string} Line dash type
 * @returns {ICanvasEditorState['pages']} New state for CanvasPage reducer
 */
export const setLineDash = (state: IPage[], action: IDashLineAction) => {
	const newState = _.cloneDeep(state);
	const page = newState[action.selectedPage as number - 1];
	const unlockedSelectedObjects = getSelectedUnlockedObjects(page, action);
	_applyLineProperties(unlockedSelectedObjects, { dash: action.dashType as DashPresets });
	return newState;
};

/**
 * Align objects
 * @param state {ICanvasEditorState['pages']} Current state for CanvasPage reducer
 * @param action {IDashLineAction} Reducer payload to align objects
 * @param action.type {String} 'ALIGN_OBJECTS'
 * @param action.selectedPage {number} current selected page
 * @param action.selectedPageObjects {number[]} Indices of current selected objects
 * @param action.alignOptions {string} align option
 * @returns {ICanvasEditorState['pages']} New state for CanvasPage reducer
 */
export const alignObjects = (state: IPage[], action: IAlignObjectAction) => {
	const { selectedPageObjects, selectedPage, alignOptions, editorHeight, editorWidth } = action;
	const newState = _.cloneDeep(state);
	const page = newState[selectedPage as number - 1];
	const toBeAlign: PageObjectType[] = [];
	// reference box for selected objects
	selectedPageObjects.forEach((selectionIndex) => {
		toBeAlign.push(page.pageObjects.filter((pageObject, index) => { return selectionIndex === index; })[0]);
	});
	let referenceBox: { off: IOffset; ext: IExtents } = manipulateDimensions.forMinOffsetsMaxExtents(toBeAlign);

	// Single object selected
	if (selectedPageObjects.length === 1) {
		referenceBox = { ext: { cy: editorHeight, cx: editorWidth }, off: { x: 0, y: 0 } };
	}
	const groupObject = page.pageObjects[selectedPageObjects[0]] as IGroupShape;

	let indicesToLoop = selectedPageObjects;
	let pageObjectsToLoop = page.pageObjects;

	if (action.groupSelectionIndices !== undefined && action.groupSelectionIndices.length > 0) {
		pageObjectsToLoop = groupObject.childObjects;
		indicesToLoop = action.groupSelectionIndices;
		if (groupObject.spPr.locked) {
			return newState;
		}
		if (action.groupSelectionIndices.length === 1) {
			referenceBox = {
				ext: {
					cx: groupObject.spPr.xfrm.ext.cx,
					cy: groupObject.spPr.xfrm.ext.cy,
				},
				off: { x: 0, y: 0 },
			};
		} else {
			const toBeAlign: PageObjectType[] = [];
			action.groupSelectionIndices.forEach((i) => {
				toBeAlign.push(groupObject.childObjects[i]);
			});
			const extents = manipulateDimensions.forMinOffsetsMaxExtents(toBeAlign);
			referenceBox.off = extents.off;
			referenceBox.ext = extents.ext;
		}
	}

	indicesToLoop.forEach((selectedObjectIndex) => {
		pageObjectsToLoop.forEach((pageObject, index) => {
			let x: number = 0;
			let y: number = 0;
			if (selectedObjectIndex === index) {
				if (pageObject.spPr.locked) {
					return;
				}
				const objectExt: IExtents = {
					cx: pageObject.spPr.xfrm.ext.cx, cy: pageObject.spPr.xfrm.ext.cy,
				};

				const dimensions = getDimensions(pageObject);
				let dimDeltaX: number;
				let dimDeltaY: number;
				switch (alignOptions) {
					case "top":
						x = pageObject.spPr.xfrm.off.x;
						const minY = _.minBy(dimensions, (p) => p.y);
						dimDeltaY = minY ? minY.y - referenceBox.off.y : 0;
						y = pageObject.spPr.xfrm.off.y - dimDeltaY;
						break;
					case "left":
						y = pageObject.spPr.xfrm.off.y;
						const minX = _.minBy(dimensions, (p) => p.x);
						dimDeltaX = minX ? minX.x - referenceBox.off.x : 0;
						x = pageObject.spPr.xfrm.off.x - dimDeltaX;
						break;
					case "right":
						y = pageObject.spPr.xfrm.off.y;
						const maxX = _.maxBy(dimensions, (p) => p.x);
						dimDeltaX = maxX ? referenceBox.ext.cx + referenceBox.off.x - maxX.x : 0;
						x = pageObject.spPr.xfrm.off.x + dimDeltaX;
						break;
					case "bottom":
						x = pageObject.spPr.xfrm.off.x;
						const maxY = _.maxBy(dimensions, (p) => p.y);
						dimDeltaY = maxY ? referenceBox.ext.cy + referenceBox.off.y - maxY.y : 0;
						y = pageObject.spPr.xfrm.off.y + dimDeltaY;
						break;
					case "hCenter":
						x = (referenceBox.off.x + referenceBox.ext.cx / 2) - (objectExt.cx / 2);
						y = pageObject.spPr.xfrm.off.y;
						break;
					case "vCenter":
						y = (referenceBox.ext.cy / 2 + referenceBox.off.y) - (objectExt.cy / 2);
						x = pageObject.spPr.xfrm.off.x;
						break;
					default:
						return;
				}

				// Align shape with reference box
				pageObject.spPr.xfrm.off.y = y;
				pageObject.spPr.xfrm.off.x = x;
			}
		});
	});
	return newState;
};

/**
 * Distribute objects
 * @param state {ICanvasEditorState['pages']} Current state for CanvasPage reducer
 * @param action {IDashLineAction} Reducer payload to translate object
 * @param action.type {String} 'DISTRIBUTE_OBJECTS'
 * @param action.selectedPage {number} current selected page
 * @param action.selectedPageObjects {number[]} Indices of current selected objects
 * @param action.dHorizontal {boolean} distribute option
 * @returns {ICanvasEditorState['pages']} New state for CanvasPage reducer
 */
export const distributeObjects = (state: IPage[], action: IDistributeObjectAction) => {
	const { selectedPage, dHorizontal } = action;
	const newState = _.cloneDeep(state);
	const page: IPage = newState[selectedPage as number - 1];
	let newDimension: { width: number; height: number };
	// Filter objects to be distributed
	const selectedObjects = page.pageObjects.filter(filterSelectedObjects(action.selectedPageObjects));
	const unlockedSelectedObjects = selectedObjects.filter(filterUnLockedObjects);
	// Helper function to return object's center either horizontal / vertical.
	const getObjectBoxCenter = (spPr: IPageObjectProperties<ITransform2D>): number => {
		if (dHorizontal) {
			return spPr.xfrm.off.x + (spPr.xfrm.ext.cx / 2);
		} else {
			return spPr.xfrm.off.y + (spPr.xfrm.ext.cy / 2);
		}
	};

	// Sort shapes as per center.
	const pageObjects: PageObjectType[] =
        _.sortBy(unlockedSelectedObjects, (pageObject) => getObjectBoxCenter(pageObject.spPr));

	// We don't want to move first and last object.
	const firstObject: PageObjectType = pageObjects[0];
	const lastObject: PageObjectType = pageObjects[pageObjects.length - 1];
	const firstObjectDimensions = getDimensions(firstObject);

	// Count of all remaining objects
	const remainingObjectCount: number = pageObjects.length - 2;

	// Calculate space per object
	const availableSpace = spaceBetweenTwoObjects(firstObject, lastObject);
	let remainingObjectSpace = 0;
	pageObjects.forEach((pageObject, index) => {
		if (pageObject === firstObject || pageObject === lastObject) {
			return;
		}
		newDimension = getBoundingBox(pageObject);
		remainingObjectSpace = remainingObjectSpace + (dHorizontal ? newDimension.width : newDimension.height);
	});
	const spacePerObject = ((dHorizontal ? availableSpace.x : availableSpace.y) - remainingObjectSpace)
        / (remainingObjectCount + 1);

	// We will start with first object offset
	const firstObjectMaxX = _.maxBy(firstObjectDimensions, (p: IPosition) => p.x) as IPosition;
	const firstObjectMaxY = _.maxBy(firstObjectDimensions, (p: IPosition) => p.y) as IPosition;
	let currentOffset = dHorizontal ? firstObjectMaxX.x : firstObjectMaxY.y;

	// distribute objects by using above manipulated information
	pageObjects.forEach((pageObject, index) => {
		if (pageObject === firstObject || pageObject === lastObject) {
			return;
		}
		const newOffset = currentOffset + spacePerObject;
		newDimension = getBoundingBox(pageObject);
		currentOffset = newOffset + (dHorizontal ? newDimension.width : newDimension.height);
		const { cx, cy } = pageObject.spPr.xfrm.ext;
		const rot = pageObject.spPr.xfrm.rot;
		// get mapped extend for rotated shapes
		const mappedExt = mapPointToShapeMatrix(pageObject, { x: cx, y: cy }, dHorizontal ? rot > 0 : rot < 0 );
		if (dHorizontal) {
			const cxDiff = (cx - Math.abs(mappedExt.x)) / 2;
			pageObject.spPr.xfrm.off.x = newOffset - cxDiff;
		} else {
			const cyDiff = (cy - Math.abs(mappedExt.y)) / 2;
			pageObject.spPr.xfrm.off.y = newOffset - cyDiff;
		}
	});
	return newState;
};

/**
 * Group current selection - called on dispatch of the GROUP action
 * @param state Current state of page reducer
 * @param action Reducer payload to group selection
 * @returns new state with current selection as grouped
 */
export const group = (state: IPage[], action: IGroupAction): IPage[] => {
	const { selectedPageObjects, selectedPage } = action;
	const newState = _.cloneDeep(state);
	if (selectedPage) {
		const page: IPage = newState[selectedPage - 1];
		const toBeGrouped = _.remove(page.pageObjects, (value, index, array) =>
			undefined !== _.find(selectedPageObjects, (selectionIndex) => selectionIndex === index));
		const result: { off: IOffset; ext: IExtents } = manipulateDimensions.forMinOffsetsMaxExtents(toBeGrouped);
		_.forEach(toBeGrouped, (child) => {
			child.spPr.xfrm.off.x = child.spPr.xfrm.off.x - result.off.x;
			child.spPr.xfrm.off.y = child.spPr.xfrm.off.y - result.off.y;
		});
		const newGroup: IGroupShape = {
			autospace: action.autospace,
			childObjects: toBeGrouped,
			isAttributePlaceholder: action.isAttributePlaceholder,
			noChangeAspect: true,
			shapeId: action.shapeId,
			spPr: {
				xfrm: {
					chExt: { cx: 0, cy: 0 },
					chOff: { x: 0, y: 0 },
					ext: { cx: result.ext.cx, cy: result.ext.cy },
					flipH: false,
					flipV: false,
					off: { x: result.off.x, y: result.off.y },
					rot: 0,
				},
			},
			type: IPageObjectTypes.GROUP_SHAPE,
		};

		page.pageObjects.splice(selectedPageObjects[0], 0, newGroup);
	}

	return newState;
};

/**
 * Ungroup current selection - called on dispatch of the UNGROUP action
 * @param state Current state of page reducer
 * @param action Reducer payload to ungroup selection
 */
export const ungroup = (state: IPage[], action: IUnGroupAction): IPage[] => {
	const { groupdShapes, selectedPage } = action;
	const newState = _.cloneDeep(state);
	if (selectedPage) {
		const page: IPage = newState[selectedPage - 1];
		_.forEach(groupdShapes, (index) => {
			const removedGs = _.remove(page.pageObjects, (po, poIndex) =>
				poIndex === index &&
                po.type === IPageObjectTypes.GROUP_SHAPE &&
                !po.spPr.locked);
			if (removedGs && removedGs.length) {
				const gs = removedGs[0] as IGroupShape;

				// pass on group shape properties to participating shapes
				const gsRot = gs.spPr.xfrm.rot || 0;
				_.forEach(gs.childObjects, (child) => {
					// Get child new offsets after post group
					child.spPr.xfrm.off = manipulateDimensions.getOffsetsPostUngroup(child, gs);

					// pass on flip settings

					child.spPr.xfrm.flipH = child.spPr.xfrm.flipH ? !gs.spPr.xfrm.flipH : gs.spPr.xfrm.flipH;
					child.spPr.xfrm.flipV = child.spPr.xfrm.flipV ? !gs.spPr.xfrm.flipV : gs.spPr.xfrm.flipV;

					if (gs.spPr.xfrm.flipH) {
						child.spPr.xfrm.rot = (360 - child.spPr.xfrm.rot);
					}
					// pass on rotation and keep it in 360 range
					child.spPr.xfrm.rot = (child.spPr.xfrm.rot + gsRot) % 360;

					// If child is picture object then maintain its source rectangle with ungroup effect
					if (child.type === IPageObjectTypes.SHAPE && child.isPicture) {
						const { srcRect } = (child.spPr.fill as IPictureFillProperties);
						srcRect.flipH = srcRect.flipH ? !gs.spPr.xfrm.flipH : gs.spPr.xfrm.flipH;
						if (gs.spPr.xfrm.flipH) {
							srcRect.rot = (360 - srcRect.rot);
						}
						srcRect.rot = (srcRect.rot + gsRot) % 360;
					}
				});

				for (let count = gs.childObjects.length - 1; count >= 0; count--) {
					page.pageObjects.splice(index, 0, gs.childObjects[count]);
				}
			}
		});
	}
	return newState;
};

/**
 * Apply format painter to shape and group shape.
 */
export const _applyFormatPainter = (targetObject: PageObjectType, sourceObject: IShape, sourceShapeTextStyle: any, placeholderTextFormat: string) => {
	if (!targetObject.imagePlaceholder && !selectionHelper.default.isPicture(targetObject) &&
        targetObject.type !== IPageObjectTypes.GROUP_SHAPE) {
		targetObject = targetObject as IShape;
		// copy line and fill properties
		if (targetObject.allowsFill && sourceObject.allowsFill) {
			targetObject.spPr.fill = _.cloneDeep(sourceObject.spPr.fill);
		}
		targetObject.spPr.ln = _.cloneDeep(sourceObject.spPr.ln);
		// apply text formatting if target is shape
		if (allowsText(targetObject) && sourceShapeTextStyle) {
			let txBody = targetObject.txBody as IShapeTextBody;
			let content: string = "";
			if (!txBody) {
				txBody = txBody || {};
				content = getDefaultFroalaHtml(TEXT_STYLE_DEFAULTS).outerHTML;
			} else {
				content = txBody.content as string;
			}

			let contentObject;
			if (targetObject.attributeKey) {
				const clearText = clearPlaceholderTextFormatting(content, placeholderTextFormat);
				if (placeholderTextFormat === PlaceholderTextFormat.ALL) {
					contentObject = applyTextStyles(targetObject, clearText, sourceShapeTextStyle.textStyles, true, true, PlaceholderTextFormat.LABEL);
					const valueStyles = _.size(sourceShapeTextStyle.valueStyles) > 0 ? sourceShapeTextStyle.valueStyles : sourceShapeTextStyle.textStyles;
					contentObject = applyTextStyles(targetObject, contentObject.innerHTML, valueStyles, true, true, PlaceholderTextFormat.VALUE);
				} else if (placeholderTextFormat === PlaceholderTextFormat.LABEL) {
					contentObject = applyTextStyles(targetObject, clearText, sourceShapeTextStyle.textStyles, true, true, PlaceholderTextFormat.LABEL);
				} else {
					contentObject = applyTextStyles(targetObject, clearText, sourceShapeTextStyle.valueStyles, true, true, PlaceholderTextFormat.VALUE);
				}
			} else {
				const clearText = clearTextFormatting(content);
				contentObject = applyTextStyles(targetObject, clearText, sourceShapeTextStyle.textStyles, true, true);
			}

			txBody.content = contentObject.innerHTML;

			if (targetObject.isTextShape && contentObject.textShapeHeight > targetObject.spPr.xfrm.ext.cy) {
				targetObject.spPr.xfrm.ext.cy = EXT_OFFSET + contentObject.textShapeHeight;
			}

			targetObject.txBody = txBody;
		}
	}
	if (targetObject.type === IPageObjectTypes.GROUP_SHAPE) {
		_.each(targetObject.childObjects, (child: any) => {
			_applyFormatPainter(child, sourceObject, sourceShapeTextStyle, placeholderTextFormat);
		});
	}
};

/**
 * Return the target shape for format
 * @param pageObjects {PageObjectType[]}
 * @param targetShapeId {string}
 * @return {PageObjectType | undefined}
 */
const getTargetObjectById = (pageObjects: PageObjectType[], targetShapeId: string): PageObjectType | undefined => {
	// get target object with targetShapeId
	const targetObject = _.find(pageObjects, (pageObject) => {
		if (pageObject.type === IPageObjectTypes.GROUP_SHAPE) {
			return getTargetObjectById(pageObject.childObjects, targetShapeId) !== undefined;
		}
		if (pageObject.shapeId === targetShapeId) {
			return true;
		}
		return false;
	});
	return targetObject;
};

/**
 * Apply format painter operation
 * @param state Current state of page reducer
 * @param action Reducer payload to apply format painter
 * @returns new state of page reducer
 */
export const applyFormatPainter =
    (state: IPage[], action: IFormatPainterAction) => {
    	const { groupSelectionIndices, placeholderTextFormat, selectedPageObjects, selectedPage, sourceShapeTextStyle, targetShapeId } = action;
    	const newState = _.cloneDeep(state);
    	const page: IPage = newState[selectedPage as number - 1];
    	let targetObject = getTargetObjectById(page.pageObjects, targetShapeId);
    	// get source object that is last object selected
    	// source object will always be first index in selectedPageObjects
    	// because format painter will be enable only if there is only one object selected
    	let sourceObject = page.pageObjects[selectedPageObjects[0]] as IShape;
    	if (groupSelectionIndices !== undefined && groupSelectionIndices.length > 0) {
    		targetObject = getTargetObjectById((sourceObject as any as IGroupShape).childObjects, targetShapeId);
    		sourceObject = (sourceObject as any as IGroupShape).childObjects[groupSelectionIndices[0]] as IShape;
    	}
    	// replace target object fill and ln properties with source object
    	if (targetObject && !selectionHelper.default.isPicture(targetObject) && !targetObject.spPr.locked) {
    		const formatPainterPlaceholderTextFormat = !!sourceObject.attributeKey ? placeholderTextFormat : PlaceholderTextFormat.ALL;
    		_applyFormatPainter(targetObject, sourceObject, sourceShapeTextStyle, formatPainterPlaceholderTextFormat);
    	}
    	return newState;
    };

/**
 * Lock / Unlock current selection
 * @param state Current state of page reducer
 * @param action Reducer payload to lock selection
 * @param action.lock {boolean} set to true to lock selected objects
 * @param action.selectedPage {number} current selected page
 * @param action.selectedPageObjects {number[]} Indices of current selected objects
 * @returns new state with current selection as locked / unlocked
 */
export const lockObjects = (state: IPage[], action: ILockObjectAction): IPage[] => {
	const { lock, selectedPage } = action;
	const newState = _.cloneDeep(state);
	const page: IPage = newState[selectedPage as number - 1];
	const selectedObjects = page.pageObjects.filter(filterSelectedObjects(action.selectedPageObjects));
	selectedObjects.forEach((selectedObject, index) => {
		if (selectedObject.spPr.locked === undefined || selectedObject.spPr.locked === !lock) {
			selectedObject.spPr.locked = lock;
		}
	});
	return newState;
};

/**
 * Delete currently selected objects.
 * @param state Current state of page reducer
 * @param action Reducer payload to delete objects.
 * @param action.selectedPage {number} current selected page
 * @param action.selectedPageObjects {number[]} Indices of current selected objects
 * @returns new state with current selection as locked / unlocked
 */
export const deleteObjects = (state: IPage[], action: IDeleteObjectsAction): IPage[] => {
	const { groupSelectionIndices, selectedPage, selectedPageObjects } = action;
	const newState = _.cloneDeep(state);
	const page: IPage = newState[selectedPage as number - 1];
	const pageObjects: PageObjectType[] = page.pageObjects;

	if (groupSelectionIndices !== undefined && groupSelectionIndices.length > 0) {
		// Find the selected group sub-object in the group and remove it.
		const groupObject = pageObjects[selectedPageObjects[0]] as IGroupShape;
		_.remove(groupObject.childObjects, (value, index, array) => {
			return !value.spPr.locked && _.includes(groupSelectionIndices, index);
		});
	} else {
		_.remove(page.pageObjects, (value, index, array) => {
			return !value.spPr.locked && _.includes(selectedPageObjects, index);
		});
	}

	return newState;
};

/**
 * Delete placeholders containing impossible keys
 * @param state Current state of page reducer
 * @param action Reducer payload to delete objects.
 * @returns new state with placeholders deleted.
 */
export const deleteKeys = (state: IPage[], action: IRemoveKeysAction): IPage[] => {
	const { selectedPage } = action;
	const newState = _.cloneDeep(state);
	const page: IPage = newState[selectedPage as number - 1];
	page.pageObjects = removePlaceholdersMatchingKeys(page.pageObjects, action.keys);
	return newState;
};

/**
 * Delete shape by shapeId.
 * @param state Current state of page reducer
 * @param action Reducer payload to delete object.
 * @returns new state
 */
export const deleteShapeById = (state: IPage[], action: IDeleteShapeByIdAction): IPage[] => {
	const { selectedPage } = action;
	const newState = _.cloneDeep(state);
	const page: IPage = newState[selectedPage as number - 1];
	_.remove(page.pageObjects, (pageObject, index, array) => action.shapeId === pageObject.shapeId);
	return newState;
};

/**
 * Scale selected objects.
 * @param state Current state of page reducer
 * @param action Reducer payload to scale objects. Action provides abstract information about scaling i.e. offset and
 * extents delta.
 */
export const scaleObjects = (state: IPage[], action: IScaleObjectAction): IPage[] => {
	const { dX, dY, dWidth, dHeight, selectedPage, cropDX, cropDY,
		isCroppingShapeScale, refExt } = action;
	const newState = _.cloneDeep(state);
	const page = newState[selectedPage as number - 1];
	const unlockedSelectedObjects = getSelectedUnlockedObjects(page, action);
	const selectedGroup = getOuterGroup(page, action);
	unlockedSelectedObjects.forEach((pageObject: any, index) => {
		// For group shapes we need to update it's child objects as well.
		if (pageObject.type === IPageObjectTypes.GROUP_SHAPE) {
			_groupScale(pageObject, dWidth, dHeight);
		}

		// turn off autospace if this is an attribute placeholder and a subelement is scaled
		if (pageObject.attributeKey && selectedGroup.autospace) {
			selectedGroup.autospace = false;
		}

		const oldWidth = pageObject.spPr.xfrm.ext.cx;
		const oldHeight = pageObject.spPr.xfrm.ext.cy;

		// Here dWidth, dHeight is in ratio of pageObject.spPr.xfrm.ext.cx, pageObject.spPr.xfrm.ext.cy resp.
		// To handle the "divide by zero" condition in the following statement we are using MIN_DIMENSION (1).
		// If pageObject.spPr.xfrm.ext.cx or pageObject.spPr.xfrm.ext.cy is
		// in between 0 and 1 then MIN_DIMENSION will not be considered.
		pageObject.spPr.xfrm.off.x += dX * (pageObject.spPr.xfrm.ext.cx || MIN_DIMENSION);
		pageObject.spPr.xfrm.ext.cx += dWidth * (pageObject.spPr.xfrm.ext.cx || MIN_DIMENSION);

		pageObject.spPr.xfrm.off.y += dY * (pageObject.spPr.xfrm.ext.cy || MIN_DIMENSION);
		pageObject.spPr.xfrm.ext.cy += dHeight * (pageObject.spPr.xfrm.ext.cy || MIN_DIMENSION);

		const spPr = pageObject.spPr as IShapeProperties;

		// We want to update shape path list only if current mode is SELECT.
		if (spPr.geometry && (spPr.geometry.type === GeometryTypes.CUSTOM || spPr.geometry.type === GeometryTypes.PEN)
            && action.mode === CanvasMode.SELECT) {
			const path = spPr.geometry.pathLst as IShapePath[];
			const width = pageObject.spPr.xfrm.ext.cx;
			const height = pageObject.spPr.xfrm.ext.cy;
			const isFlipped: boolean = (width < 0 || height < 0) ? true : false;
			const penGeometry = spPr.geometry as IPenGeometry;
			util.updateShapePathListPercentage(path, dWidth, dHeight, isFlipped, penGeometry.handlePoints);
		}

		// cropDX and cropDY corrections when cropping shape is flipped. This avoids cropping image stretching.
		let flipCropCorrectionX = 0;
		let flipCropCorrectionY = 0;

		if (pageObject.spPr.xfrm.ext.cy < 0) {
			flipCropCorrectionY = pageObject.spPr.xfrm.ext.cy;
			pageObject.spPr.xfrm.off.y += pageObject.spPr.xfrm.ext.cy;
			pageObject.spPr.xfrm.ext.cy *= -1;
			pageObject.spPr.xfrm.flipH = !pageObject.spPr.xfrm.flipH;
			pageObject.spPr.xfrm.flipV = !pageObject.spPr.xfrm.flipV;
			pageObject.spPr.xfrm.rot = (pageObject.spPr.xfrm.rot + 180) % 360;
			// Rotation and flip to srcRect, too, if its an image object and we are not in cropping mode
			if (pageObject.type === IPageObjectTypes.SHAPE && pageObject.isPicture && !isCroppingShapeScale) {
				const { srcRect } = (pageObject.spPr.fill as IPictureFillProperties);
				srcRect.flipH = !srcRect.flipH;
				srcRect.rot = (srcRect.rot + 180) % 360;
			}
		}

		if (pageObject.spPr.xfrm.ext.cx < 0) {
			flipCropCorrectionX = pageObject.spPr.xfrm.ext.cx;
			pageObject.spPr.xfrm.off.x += pageObject.spPr.xfrm.ext.cx;
			pageObject.spPr.xfrm.ext.cx *= -1;
			pageObject.spPr.xfrm.flipH = !pageObject.spPr.xfrm.flipH;
			// Rotation and flip to srcRect, too, if its an image object and we are not in cropping mode
			if (pageObject.type === IPageObjectTypes.SHAPE && pageObject.isPicture && !isCroppingShapeScale) {
				const { srcRect } = (pageObject.spPr.fill as IPictureFillProperties);
				srcRect.flipH = !srcRect.flipH;
			}
		}
		// If we have picture shape object then its cropping shape is being scaled, cropped image remains as it is,
		// and only cropping shape changes its dimensions (as opposed to shape with image being scaled when no picture
		// edit mode)
		if (pageObject.type === IPageObjectTypes.SHAPE && pageObject.isPicture && isCroppingShapeScale) {
			const srcRect = (pageObject.spPr.fill as IPictureFillProperties).srcRect;

			const { cx, cy } = pageObject.spPr.xfrm.ext;

			// Maintained the source rectangle reference extents(refExt) by assigning refExt value.
			if (refExt) {
				srcRect.refExt = refExt;
			}
			// Considering srcRect is undefined then assigning pageObject extents values (pageObject.spPr.xfrm.ext).
			if (!srcRect.refExt) {
				srcRect.refExt = pageObject.spPr.xfrm.ext;
			}

			// Get pixel level source rect left value with respect to old width,
			// consider cropDX in to it, and then convert it to percentage with respect to new width
			let lPx = oldWidth * srcRect.l / PERCENTAGE_HUNDRED;
			lPx = lPx - cropDX - flipCropCorrectionX;
			// Use MIN_DIMENSION to avoid "divide by zero" condition, iff pageObject.spPr.xfrm.ext.cx is zero.
			srcRect.l = (lPx * PERCENTAGE_HUNDRED / (cx || MIN_DIMENSION));

			// Get pixel level source rect top value with respect to old height,
			// consider cropDY into it, and then convert it to percentage with respect to new height
			let tPx = oldHeight * srcRect.t / PERCENTAGE_HUNDRED;
			tPx = tPx - cropDY - flipCropCorrectionY;
			// Use MIN_DIMENSION to avoid "divide by zero" condition, iff pageObject.spPr.xfrm.ext.cy is zero.
			srcRect.t = tPx * PERCENTAGE_HUNDRED / (cy || MIN_DIMENSION);

			// Get pixel level source rect right value with respect to old width,
			// consider cropDX and dWidth into it, and then convert it to percentage with respect to new width
			let rPx = oldWidth * srcRect.r / PERCENTAGE_HUNDRED;
			rPx = rPx + (dWidth * oldWidth) + cropDX - flipCropCorrectionX;
			// Use MIN_DIMENSION to avoid "divide by zero" condition, iff pageObject.spPr.xfrm.ext.cx is zero.
			srcRect.r = rPx * PERCENTAGE_HUNDRED / (cx || MIN_DIMENSION);

			// Get pixel level source rect bottom value with respect to old height,
			// consider cropDY and dHeight into it, and then convert it to percentage with respect to new height
			let bPx = oldHeight * srcRect.b / PERCENTAGE_HUNDRED;
			bPx = bPx + (dHeight * oldHeight) + cropDY - flipCropCorrectionY;
			// Use MIN_DIMENSION to avoid "divide by zero" condition, iff pageObject.spPr.xfrm.ext.cy is zero.
			srcRect.b = bPx * PERCENTAGE_HUNDRED / (cy || MIN_DIMENSION);
		}
	});
	return newState;
};

/**
 * Scale cropped image.
 * @param state Current state of page reducer
 * @param action Reducer payload to scale cropped image.
 * Action provides abstract information about scaling i.e. offset and extents delta.
 */
export const scaleCroppedImage = (state: IPage[], action: IScaleObjectAction): IPage[] => {
	const { selectedPage, cropDX, cropDY, cropDWidth, cropDHeight } = action;
	const newState = _.cloneDeep(state);
	const page = newState[selectedPage as number - 1];
	const unlockedSelectedObjects = getSelectedUnlockedObjects(page, action);
	unlockedSelectedObjects.forEach((pageObject, index) => {
		// Cropped image being scaled, cropping shape remains as it is
		if (pageObject.type === IPageObjectTypes.SHAPE && pageObject.isPicture) {
			const srcRect = (pageObject.spPr.fill as IPictureFillProperties).srcRect;

			const { cx, cy } = pageObject.spPr.xfrm.ext;

			// Get pixel level source rect left value with respect to width,
			// consider cropDX in to it, and then convert it to percentage with respect width
			let lPx = cx * srcRect.l / PERCENTAGE_HUNDRED;
			lPx = lPx + cropDX;
			srcRect.l = (lPx * PERCENTAGE_HUNDRED / (cx || MIN_DIMENSION));

			// Get pixel level source rect top value with respect to height,
			// consider cropDY into it, and then convert it to percentage with respect to height
			let tPx = cy * srcRect.t / PERCENTAGE_HUNDRED;
			tPx = tPx + cropDY;
			srcRect.t = tPx * PERCENTAGE_HUNDRED / (cy || MIN_DIMENSION);

			// Get pixel level source rect right value with respect to width,
			// consider cropDX and dWidth into it, and then convert it to percentage with respect to width
			let rPx = cx * srcRect.r / PERCENTAGE_HUNDRED;
			rPx = rPx - (cropDWidth * cx) - cropDX;
			srcRect.r = rPx * PERCENTAGE_HUNDRED / (cx || MIN_DIMENSION);

			// Get pixel level source rect bottom value with respect to height,
			// consider cropDY and dHeight into it, and then convert it to percentage with respect to height
			let bPx = cy * srcRect.b / PERCENTAGE_HUNDRED;
			bPx = bPx - (cropDHeight * cy) - cropDY;
			srcRect.b = bPx * PERCENTAGE_HUNDRED / (cy || MIN_DIMENSION);

			// Case where ghost image is resized (scaled) where image boundaries are crossed.
			// Rotate srcRect by 180 and manage new values
			if (cx < lPx + rPx || cy < bPx + tPx) {
				srcRect.rot = (srcRect.rot + 180) % 360;
			}
			if (cx < lPx + rPx ) {
				const gw =  lPx + rPx - (cx || MIN_DIMENSION);
				const newRpx = lPx - gw;
				const newLpx = rPx - gw;
				srcRect.l = (newLpx * PERCENTAGE_HUNDRED / (cx || MIN_DIMENSION));
				srcRect.r = (newRpx * PERCENTAGE_HUNDRED / (cx || MIN_DIMENSION));
			}
			if (cy < bPx + tPx) {
				const gh =  tPx + bPx - (cy || MIN_DIMENSION);
				const newBpx = tPx - gh;
				const newTpx = bPx - gh;
				srcRect.t = (newTpx * PERCENTAGE_HUNDRED / (cy || MIN_DIMENSION));
				srcRect.b = (newBpx * PERCENTAGE_HUNDRED / (cy || MIN_DIMENSION));
			}
		}
	});
	return newState;
};

/**
 * Change sort order of currently selected page objects.
 * @param state Current state of page reducer
 * @param action Reducer payload to change sort order of selected objects.
 * @param action.selectedPage {number} current selected page
 * @param action.selectedPageObjects {number[]} Indices of current selected objects
 * @returns new state with updated indices of pageObjects
 */
export const changePageObjectOrder = (state: IPage[], action: IChangeOrderAction): IPage[] => {
	const { selectedPage } = action;
	const newState = _.cloneDeep(state);
	const page: IPage = newState[selectedPage as number - 1];
	page.pageObjects = action.pageObjects;
	const groupSelectionIndices = action.groupSelectionIndices;
	if (groupSelectionIndices !== undefined && groupSelectionIndices.length > 0) {
		const groupObject = page.pageObjects[action.selectedPageObjects[0]] as IGroupShape;
		groupObject.childObjects = moveObjectsToFront(groupObject.childObjects, groupSelectionIndices, action.bFront);
	}
	return newState;
};

/**
 * Set new path list to the selected shape.
 * @param state Current state of page reducer.
 * @param action Reducer payload to set curve points for the selected shape.
 * @param action.pathLst {IShapePath[]} New shape path for the selected shape.
 */
export const setCurvePoints = (state: IPage[], action: ISetCurvePointsAction): IPage[] => {
	const { selectedPage, pathLst } = action;
	const newState = _.cloneDeep(state);
	const page = newState[selectedPage as number - 1];
	const unlockedSelectedObjects = getSelectedUnlockedObjects(page, action);

	if (unlockedSelectedObjects && unlockedSelectedObjects.length) {
		// safely assume that changes should be done to the first object
		const shapeToModify = unlockedSelectedObjects[0] as IShape;
		const geometry = shapeToModify.spPr.geometry as ICustomGeometry;
		const penGeometry = shapeToModify.spPr.geometry as IPenGeometry;
		geometry.pathLst = pathLst;

		if (action.handlePoints) {
			penGeometry.handlePoints = action.handlePoints;
		}
	}

	return newState;
};

/**
 * Set new path list to the selected shape.
 * @param state Current state of page reducer.
 * @param action Reducer payload to set path for the selected shape
 * @param action.pathLst {IShapePath[]} New shape path for the selected shape.
 */
export const setGeometry = (state: IPage[], action: ISetGeometryAction): IPage[] => {
	const { selectedPage, pathLst } = action;
	const newState = _.cloneDeep(state);
	const page = newState[selectedPage as number - 1];
	const unlockedSelectedObjects = getSelectedUnlockedObjects(page, action);

	if (unlockedSelectedObjects && unlockedSelectedObjects.length) {
		// safely assume that changes should be done to the first object
		const geometry = (unlockedSelectedObjects[0] as IShape).spPr.geometry as ICustomGeometry;
		geometry.pathLst = pathLst;
	}

	return newState;
};

/**
 * Set new handle points for the selected shape.
 * @param state Current state of page reducer.
 * @param action Reducer payload to set handle points for the selected shape.
 * @param action.handlePoints {IHandlePoint[]} New handle points for the selected shape.
 */
export const setHandlePoints = (state: IPage[], action: ISetCurvePointsAction): IPage[] => {
	const { selectedPage, pointLst } = action;
	const newState = _.cloneDeep(state);
	const page = newState[selectedPage as number - 1];
	const unlockedSelectedObjects = getSelectedUnlockedObjects(page, action);

	if (unlockedSelectedObjects && unlockedSelectedObjects.length) {
		// safely assume that changes should be done to the first object
		const geometry = (unlockedSelectedObjects[0] as IShape).spPr.geometry as IPenGeometry;
		geometry.handlePoints = pointLst;
	}

	return newState;
};

/**
 * Set offsets to selected object
 * Note: Ideally, it would be applied to single shape. However, api is open to accept
 * multiple shapes if provided.
 * @param state Current state of page reducer
 * @param action Reducer payload to set offsets
 */
export const setOffsets = (state: IPage[], action: ISetOffsetsAction): IPage[] => {
	const { selectedPageObjects, selectedPage, off } = action;
	const newState = _.cloneDeep(state);
	if (selectedPage && selectedPageObjects && off) {
		const page: IPage = newState[selectedPage - 1];

		const unlockedSelectedObjects = getSelectedUnlockedObjects(page, action);
		unlockedSelectedObjects.forEach((selectedObject) => {
			selectedObject.spPr.xfrm.off = off;
		});
	}
	return newState;
};

/**
 * Set text content for the shape.
 *
 * @param state {ICanvasEditorState} Current state of page reducer.
 * @param action {ISetShapeTextContentAction} Reducer payload.
 */
export const setShapeTextContent = (state: IPage[], action: ISetShapeTextContentAction):
    IPage[] => {
	const { groupSelectionIndices, selectedPageObjects, selectedPage, content } = action;
	const newState = _.cloneDeep(state);
	if (selectedPage && selectedPageObjects) {
		const page: IPage = newState[selectedPage - 1];
		let selectedObject = page.pageObjects[action.objectKey];
		if (groupSelectionIndices !== undefined && groupSelectionIndices.length > 0) {
			selectedObject = (selectedObject as IGroupShape).childObjects[groupSelectionIndices[0]];
		}
		if (selectedObject.type === IPageObjectTypes.SHAPE && !selectedObject.spPr.locked) {
			const shape: IShape = selectedObject as IShape;
			shape.txBody = shape.txBody || {};
			shape.txBody.content = content;
		}
	}
	return newState;
};

/**
 * Apply text style to group child's
 * @param groupObject {IGroupShape}
 * @param style {string}
 * @param value {string}
 * @param isApply {boolean | undefined}
 */
const _groupTextStyle = (
	groupObject: IGroupShape,
	style: string,
	value: string,
	isApply: boolean,
	placeholderTextFormat: string,
) => {
	const defaultDom = getDefaultFroalaHtml(TEXT_STYLE_DEFAULTS).outerHTML;
	_.each(groupObject.childObjects, (childObject) => {
		if (
			childObject.type === IPageObjectTypes.SHAPE &&
                !childObject.imagePlaceholder &&
                !childObject.isPicture
		) {
			childObject.txBody = childObject.txBody || {};
			const textStyle = {};
			textStyle[style] = value;
			const content = childObject.txBody.content || defaultDom;
			const contentObject = applyTextStyles(childObject, content, textStyle, isApply, false, placeholderTextFormat);
			childObject.txBody.content = contentObject.innerHTML;
			util.applyAutosizeExtents(childObject, childObject.spPr.autosize !== AutosizeMode.FIXED_SIZE);
		}
		if (childObject.type === IPageObjectTypes.GROUP_SHAPE) {
			_groupTextStyle(childObject, style, value, isApply, placeholderTextFormat);
		}
	});
};

/**
 * Set shape text style
 * @param state {ICanvasEditorState} Current state of page reducer.
 * @param action {ISetShapeTextStyle} Reducer payload.
 */
export const applyTextToSelectedShape = (state: IPage[], action: ISetShapeTextStyle): IPage[] => {
	const { selectedPage, style, value, isApply, placeholderTextFormat } = action;
	const newState = _.cloneDeep(state);
	const page: IPage = newState[selectedPage as number - 1];
	const unlockedSelectedObjects = getSelectedUnlockedObjects(page, action);
	const defaultDom = getDefaultFroalaHtml(TEXT_STYLE_DEFAULTS).outerHTML;
	unlockedSelectedObjects.forEach((pageObject: any, index) => {
		if (pageObject.type === IPageObjectTypes.SHAPE && !pageObject.imagePlaceholder) {
			const shape: IShape = pageObject as IShape;
			const content = !shape.txBody ? defaultDom : (shape.txBody.content || defaultDom);
			const textStyle = {};
			textStyle[style] = value;
			shape.txBody = shape.txBody || {};
			const contentObject = applyTextStyles(shape, content, textStyle, isApply, false, placeholderTextFormat);
			shape.txBody.content = contentObject.innerHTML;
			// if autosize is not fixed and the shape is text, set height to fit content
			if (shape.spPr.autosize !== AutosizeMode.FIXED_SIZE && shape.isTextShape) {
				const offset = (shape.attributeKey) ? PADDING_INSIDE_ATTRIBUTE_PLACEHOLDER_SHAPES : EXT_OFFSET;
				const { ext } = shape.spPr.xfrm;
				ext.cx = contentObject.textShapeWidth;
				ext.cy = contentObject.textShapeHeight + offset;
			}
		}
		// if selected shape is group shape apply styles to child shape if it has SHAPE type
		if (pageObject.type === IPageObjectTypes.GROUP_SHAPE && !pageObject.spPr.locked) {
			_groupTextStyle(pageObject, style, value, isApply, placeholderTextFormat);
		}
	});
	_applyAutospace(unlockedSelectedObjects, page, action);
	return newState;
};

/**
 * Apply autospace to all applicable shapes and groups
 */
const _applyAutospace = (pageObjects: PageObjectType[], page: IPage, action: ISetShapeTextStyle) => {
	pageObjects.forEach((pageObject: PageObjectType) => {
		const groupObject = pageObject as IGroupShape;
		if (groupObject.childObjects && !groupObject.isAttributePlaceholder) {
			_applyAutospace(groupObject.childObjects, page, action);
		} else if (groupObject.childObjects && groupObject.isAttributePlaceholder) {
			util.resetPlaceholderGroup(groupObject);
		} else {
			const childObject = pageObject as IShape;
			util.applyAutosizeExtents(childObject);
		}
	});
};

/**
 * Scale image relative to frame
 * @param state {ICanvasEditorState['pages']} Current state for CanvasPage reducer
 * @param action {IScaleImageToFrameAction} Reducer payload to scale image
 * @returns {ICanvasEditorState['pages']} New state for CanvasPage reducer
 */
export const scaleImageToFrame = (state: IPage[], action: IScaleImageToFrameAction) => {
	const newState = _.cloneDeep(state);
	const page = newState[action.selectedPage as number - 1];
	const unlockedSelectedObjects = getSelectedUnlockedObjects(page, action);

	_.each(unlockedSelectedObjects, (object, index) => {
		const pageObject = object as IPicture;
		const aspectRatio = action.originalAspectRatios[index];
		const rect = (pageObject.spPr.fill as IPictureFillProperties).srcRect;
		const ext = pageObject.spPr.xfrm.ext;
		const widthRatio = aspectRatio.w / ext.cx;
		const heightRatio = aspectRatio.h / ext.cy;
		let scaleHeight = false;

		if (action.scalingType === "original") {
			let overflow = (ext.cx - aspectRatio.w) / ext.cx;

			rect.r = overflow * PERCENTAGE_HUNDRED / 2;
			rect.l = overflow * PERCENTAGE_HUNDRED / 2;

			overflow = (ext.cy - aspectRatio.h) / ext.cy;

			rect.t = overflow * PERCENTAGE_HUNDRED / 2;
			rect.b = overflow * PERCENTAGE_HUNDRED / 2;

			return newState;
		} else if (action.scalingType === "reset") {
			const imageExtents = getImageExtents(pageObject as any as IShape);
			const spPr = pageObject.spPr;
			rect.flipH = false;
			rect.rot = 0;
			spPr.xfrm.rot = 0;
			spPr.xfrm.flipH = false;

			const radians = spPr.xfrm.rot * (Math.PI / 180) * -1;
			const rectX = ext.cx * rect.l / PERCENTAGE_HUNDRED;
			const rectY = ext.cy * rect.t / PERCENTAGE_HUNDRED;
			const translateX = rectX * Math.cos(radians) - rectY * Math.sin(radians);
			const translateY = rectX * Math.sin(radians) + rectY * Math.cos(radians);

			ext.cx = imageExtents.cx;
			ext.cy = imageExtents.cy;

			spPr.xfrm.off.x = spPr.xfrm.off.x + translateX;
			spPr.xfrm.off.y = spPr.xfrm.off.y + translateY;

			spPr.geometry.presetShape = ShapeType.rect;
			spPr.geometry.type = GeometryTypes.PRESET;

			rect.b = 0;
			rect.l = 0;
			rect.r = 0;
			rect.t = 0;

			return newState;
		} else if (action.scalingType === "fit" && widthRatio > heightRatio) {
			scaleHeight = true;
		} else if (action.scalingType === "fill" && heightRatio > widthRatio) {
			scaleHeight = true;
		}

		if (scaleHeight) {
			rect.r = 0;
			rect.l = 0;

			const newHeight = aspectRatio.h / widthRatio;
			const overflow = (ext.cy - newHeight) / ext.cy;

			rect.t = overflow * PERCENTAGE_HUNDRED / 2;
			rect.b = overflow * PERCENTAGE_HUNDRED / 2;
		} else {
			rect.t = 0;
			rect.b = 0;

			const newWidth = aspectRatio.w / heightRatio;
			const overflow = (ext.cx - newWidth) / ext.cx;

			rect.r = overflow * PERCENTAGE_HUNDRED / 2;
			rect.l = overflow * PERCENTAGE_HUNDRED / 2;
		}
	});
	return newState;
};

/**
 * Scale objects extends
 * @param state {ICanvasEditorState['pages']} Current state for CanvasPage reducer
 * @param action {IScaleObjectExtendsAction} Reducer payload to scale object
 * @returns {ICanvasEditorState['pages']} New state for CanvasPage reducer
 */
export const scaleObjectExtends = (state: IPage[], action: IScaleObjectExtendsAction) => {
	const newState = _.cloneDeep(state);
	const page = newState[action.selectedPage as number - 1];
	const unlockedSelectedObjects = getSelectedUnlockedObjects(page, action);
	unlockedSelectedObjects.forEach((pageObject, index) => {
		pageObject.spPr.xfrm.ext.cx = pageObject.spPr.xfrm.ext.cx + action.cxOffset;
		pageObject.spPr.xfrm.ext.cy = pageObject.spPr.xfrm.ext.cy + action.cyOffset;
	});
	return newState;
};

/**
 * Set objects extends
 * @param state {ICanvasEditorState['pages']} Current state for CanvasPage reducer
 * @param action {ISetObjectExtendsAction} Reducer payload to scale object
 * @returns {ICanvasEditorState['pages']} New state for CanvasPage reducer
 */
export const setObjectExtends = (state: IPage[], action: ISetObjectExtendsAction) => {
	const newState = _.cloneDeep(state);
	const page = newState[action.selectedPage as number - 1];
	const unlockedSelectedObjects = getSelectedUnlockedObjects(page, action);
	unlockedSelectedObjects.forEach((pageObject, index) => {
		pageObject.spPr.xfrm.ext.cx = action.extend.cx || pageObject.spPr.xfrm.ext.cx;
		pageObject.spPr.xfrm.ext.cy = action.extend.cy || pageObject.spPr.xfrm.ext.cy;
	});
	return newState;
};

/**
 * Reset placeholder extends
 * @param state {ICanvasEditorState['pages']} Current state for CanvasPage reducer
 * @param action {resetPlaceholderExtends} Reducer payload to reset placeholder extends
 * @returns {ICanvasEditorState['pages']} New state for CanvasPage reducer
 */
export const resetPlaceholderExtends = (state: IPage[], action: IResetPlaceholderExtendsAction) => {
	const newState = _.cloneDeep(state);

	// if a placeholder group shape is directly passed in, use it instead
	const { placeholder } = action;
	if (placeholder) {
		util.resetPlaceholderGroup(placeholder);
		return newState;
	}

	// look for a selected group using the page and action
	const page = newState[action.selectedPage as number - 1];
	const selectedGroup = getOuterGroup(page, action) as IGroupShape;
	util.resetPlaceholderGroup(selectedGroup);

	return newState;
};

/**
 * Edit child objects inside placeholder, payload holds childObjects
 * @param state {ICanvasEditorState['pages']} Current state for CanvasPage reducer
 * @param action {IEditPlaceholderAction} Reducer payload to edit placeholder
 * @returns {ICanvasEditorState['pages']} New state for CanvasPage reducer
 */
export const editPlaceholder = (state: IPage[], action: IEditPlaceholderAction) => {
	const newState = _.cloneDeep(state);
	const { groupSelectionIndices, selectedPageObjects, selectedPage } = action;
	const page = newState[selectedPage as number - 1];
	let groupObject = page.pageObjects[selectedPageObjects[0]] as IGroupShape;
	// If we are inside of a group, need to find the group childObject we are working on.
	if (groupSelectionIndices !== undefined && groupSelectionIndices.length > 0) {
		groupObject = groupObject.childObjects[groupSelectionIndices[0]] as any as IGroupShape;
		// As to not use the group's CY, adjust it in relation to the sum of the child heights.
		groupObject.spPr.xfrm.ext.cy = _.sum(_.map(action.pageObjects, (child) =>
			child.spPr.xfrm.ext.cy + SPACE_BETWEEN_ATTRIBUTE_PLACEHOLDER_SHAPES));
	}

	groupObject.childObjects = [];
	_.assign(groupObject.childObjects, action.pageObjects);

	util.resetPlaceholderGroup(groupObject);

	return newState;
};

/**
 * Replace current pages with sorted pages
 * @param state {ICanvasEditorState['pages']} Current state for CanvasPage reducer
 * @param action {IScaleObjectExtendsAction} Reducer payload to sort pages
 * @returns {ICanvasEditorState['pages']} New state for CanvasPage reducer
 */
export const sortPages = (state: IPage[], action: ISortPagesAction) => {
	return action.sortedPages;
};

/**
 * Remove migrated extends reducer
 * @param state {IPage[]} Current state for CanvasPage reducer
 * @param action {IRemoveMigratedExtents} Reducer payload to remove migrated extends
 * @returns {IPage[]} New state for CanvasPage reducer
 */
export const removeMigratedExtents = (state: IPage[], action: IRemoveMigratedExtents) => {
	const newState = _.cloneDeep(state);
	const page = newState[0];
	const { shapeIds } = action;
	_.each(page.pageObjects, (po) => {
		if (shapeIds.indexOf(po.shapeId) !== -1 && po.type === IPageObjectTypes.SHAPE && po.txBody) {
			delete po.txBody.migrationExtents;
		}
	});
	return newState;
};

/**
 * Toggle autospace
 * @param state Current state for CanvasPage reducer
 * @param action Reducer payload
 */
export const togglePlaceholderAutospace = (state: IPage[], action: IToggleAutospace): IPage[] => {
	const newState = _.cloneDeep(state);
	const page = newState[action.selectedPage as number - 1];
	const unlockedSelectedObjects = getSelectedUnlockedObjects(page, action);
	const _allAreTrue = (pageObjects) => {
		return pageObjects.every((pageObject) => {
			if (pageObject.childObjects && !pageObject.isAttributePlaceholder) {
				return _allAreTrue(pageObject.childObjects);
			}
			if (pageObject.childObjects && pageObject.isAttributePlaceholder) {
				return pageObject.autospace;
			}
			return false;
		});
	};
	_togglePlaceholderAutospace(unlockedSelectedObjects, _allAreTrue(unlockedSelectedObjects) ? false : true);
	return newState;
};

/**
 * Apply placeholder autospace to all nested placeholders
 * @param {PageObjectType[]} pageObjects
 * @param {boolean} autospace
 */
const _togglePlaceholderAutospace = (pageObjects: PageObjectType[], autospace: boolean) => {
	pageObjects.forEach((pageObject: any) => {
		if (pageObject.childObjects && !pageObject.isAttributePlaceholder) {
			_togglePlaceholderAutospace(pageObject.childObjects, autospace);
		} else if (pageObject.childObjects && pageObject.isAttributePlaceholder) {
			pageObject.autospace = autospace;
			if (autospace) {
				util.resetPlaceholderGroup(pageObject);
			}
		}
	});
};

/**
 * Toggle shape label visibility
 * @param state Current state for CanvasPage reducer
 * @param action Reducer payload
 */
export const togglePlaceholderLabels = (state: IPage[], action: IToggleLabels): IPage[] => {
	const newState = _.cloneDeep(state);
	const page = newState[action.selectedPage as number - 1];
	const unlockedSelectedObjects = getSelectedUnlockedObjects(page, action);
	const _allAreTrue = (pageObjects) => {
		return pageObjects.every((pageObject: any) => {
			if (pageObject.childObjects) {
				return _allAreTrue(pageObject.childObjects);
			}
			return (pageObject.showLabel || !pageObject.isTextShape);
		});
	};
	_togglePlaceholderLabels(unlockedSelectedObjects, _allAreTrue(unlockedSelectedObjects) ? false : true);
	_applyAutospace(unlockedSelectedObjects, page, action as ISetShapeTextStyle);
	return newState;
};

/**
 * Apply shape label visibility to all nested placeholder text shapes
 * @param {PageObjectType[]} pageObjects
 * @param {boolean} showLabel
 */
const _togglePlaceholderLabels = (pageObjects: PageObjectType[], showLabel: boolean) => {
	pageObjects.forEach((pageObject: any) => {
		if (pageObject.childObjects) {
			_togglePlaceholderLabels(pageObject.childObjects, showLabel);
		} else if (pageObject.isTextShape) {
			util.togglePlaceholderLabel(pageObject, showLabel);
		}
		if (pageObject.autospace) {
			util.resetPlaceholderGroup(pageObject);
		}
	});
};

/**
 * Hide / Unhide Placeholder Labels based on Edit Mode and Value content
 */
export const togglePlaceholderLabelsOptionally = (state: IPage[], action: IToggleLabelsOptionally) => {
	const pages = _.cloneDeep(state);
	pages.forEach((page) => {
		_togglePlaceholderLabelsOptionally(page.pageObjects, action.canvasEditMode);
	});
	return pages;
};

/**
 * Hide / Unhide Placeholder Labels based on Edit Mode and Value content (recursive)
 */
const _togglePlaceholderLabelsOptionally = (pageObjects: PageObjectType[], editMode: boolean) => {
	pageObjects.forEach((pageObject: any) => {
		if (pageObject.childObjects) {
			_togglePlaceholderLabelsOptionally(pageObject.childObjects, editMode);
		} else if (pageObject.isTextShape) {
			util.togglePlaceholderLabelOptionally(pageObject, editMode);
		}
	});
};